import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './PartyPortalModal.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// PartyPortalModal — painel mostrado ao clicar no Portal do Guardião estando
// em grupo (>1 membro). Puramente informativo: mostra quem já chegou perto do
// portal em tempo real (posições vêm do WebSocket do Hub — ver HubMap.tsx).
// A batalha começa sozinha assim que TODOS estiverem presentes — é
// HubMap.tsx quem detecta isso e chama partyApi.start() pelo líder; este
// modal só reflete o estado, não decide nada.
// ─────────────────────────────────────────────────────────────────────────────

export interface PartyPortalMember {
  userId:   string;
  name:     string;
  isLeader: boolean;
  isMe:     boolean;
  status:   'here' | 'enroute' | 'offline';
}

interface Props {
  members: PartyPortalMember[];
  allGathered: boolean;
  onClose: () => void;
}

export default function PartyPortalModal({ members, allGathered, onClose }: Props) {
  const { t } = useTranslation();

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.box} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('party.portal.title' as any)}>
        <div className={styles.header}>
          <h3 className={styles.title}>🌀 {t('party.portal.title' as any)}</h3>
          <button className={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        <p className={styles.hint}>{t('party.portal.hint' as any)}</p>

        <div className={styles.list}>
          {members.map(m => (
            <div key={m.userId} className={`${styles.row} ${m.status === 'here' ? styles.rowHere : ''}`}>
              <span className={`${styles.dot} ${
                m.status === 'here' ? styles.dotHere : m.status === 'offline' ? styles.dotOffline : styles.dotEnroute
              }`} />
              <span className={styles.name}>
                {m.isLeader ? '👑 ' : ''}{m.name}{m.isMe ? ` ${t('party.you')}` : ''}
              </span>
              <span className={styles.status}>
                {m.status === 'here'
                  ? t('party.portal.here' as any)
                  : m.status === 'offline'
                    ? t('party.portal.offline' as any)
                    : t('party.portal.notHere' as any)}
              </span>
            </div>
          ))}
        </div>

        {allGathered && (
          <p className={styles.starting}>⚔ {t('party.portal.starting' as any)}</p>
        )}
      </div>
    </div>,
    document.body,
  );
}
